import React, { useMemo } from 'react';

import Guidance from '../common/Guidance';
import useQueryLookupTitles from '../../hooks/useQueryLookupTitles';
import { conditionTermsOf, describeOmitted, describeQueryWarning } from '../../utils/queryWarnings';

export const FILTER_DRIFT_TITLE = 'This filter names something that’s gone';
export const FILTER_DRIFT_EDIT_LABEL = 'Edit filter';
export const FILTER_DRIFT_GUIDE = 'finding-and-lists/build-a-filter';

/**
 * The list's warning when its filter names something the collection no
 * longer has (docs/plans/analytics.md PR AF, ruled d24 — "Don't break the
 * list, but put a warning message that explains what was removed and how it
 * impacted the list"). ONE calm `Guidance` in the caution tone under the
 * toolbar: a sentence per condition, straight from the server's report
 * (`X-Query-Warnings`, utils/queryWarnings.js), "N more…" for what the
 * header had no room for, and the door that fixes it. Each sentence is about
 * its CONDITION — what the server's effect describes — never the whole list.
 *
 * It never blocks, empties or reorders the list — the list under it is the
 * one the server answered. **Edit filter** opens the filter builder, and is
 * offered only when a warned condition is in the person's OWN filter
 * (`query`): a condition from a card's worklist filter is explained, but it
 * is not theirs to edit here. Not dismissible: it goes when the filter no
 * longer names anything gone.
 */
export default function FilterDriftWarning({
	warnings = [],
	omitted = 0,
	query = '',
	fields = [],
	resolveCard = null,
	onEditFilter = null,
	className = 'items-page__alert',
}) {
	// A lookup condition stores an item id; the sentence says the item's title.
	const termsQuery = useMemo(() => (warnings || []).map(w => w.term).join(' and '), [warnings]);
	const { titleFor } = useQueryLookupTitles(termsQuery, fields);

	const lines = useMemo(
		() => [
			...(warnings || []).map(warning => describeQueryWarning(warning, { fields, resolveCard, titleFor })),
			describeOmitted(omitted),
		].filter(Boolean),
		[warnings, omitted, fields, resolveCard, titleFor]
	);
	const editable = useMemo(() => {
		const own = conditionTermsOf(query);
		return (warnings || []).some(warning => own.has(warning.term));
	}, [warnings, query]);

	if (!lines.length) return null;
	return (
		<Guidance
			tone="caution"
			className={['filter-drift', className].filter(Boolean).join(' ')}
			title={FILTER_DRIFT_TITLE}
			why={lines}
			action={editable && typeof onEditFilter === 'function' ? { label: FILTER_DRIFT_EDIT_LABEL, onClick: onEditFilter } : null}
			links={[FILTER_DRIFT_GUIDE]}
		/>
	);
}
